import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { updateError } from './user';
// utils
import { callApi } from '../../utils/apiCall';
import { localStorageSetItem, localStorageGetItem } from '../../utils/storage-available';
import { getListData, getReferralData, getSourceTypeData, getReminderMethodData, getReasonsListData, getContactMethodListData, getSalutationOptions, getFinanceTermOptions } from '../../utils/getReferralData';

// ----------------------------------------------------------------

const safeParse = (key, transform) => {
    try {
        const raw = localStorageGetItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return transform ? transform(parsed) : parsed;
    } catch { return []; }
};

const initialState = {
    preferredContactMethodData: safeParse('PreferredContactMethodData', getListData),
    referral: safeParse('ReferralData', getReferralData),
    sourceType: safeParse('SourceTypeData', getSourceTypeData),
    reminderMethod: safeParse('ReminderMethodData', getReminderMethodData),
    reminderPriority: safeParse('ReminderPriorityData', getListData),
    reminderGroup: safeParse('ReminderGroupData'),
    // V2 Task* enums — lowercase values, distinct from Reminder* enums.
    // BE PR #5550 emits these as separate OpenAPI components so the FE
    // can route to the right enum per endpoint (V1 /reminders/ vs V2 /tasks/).
    taskMethod: safeParse('TaskMethodData'),
    taskPriority: safeParse('TaskPriorityData'),
    taskCategory: safeParse('TaskCategoryData'),
    reasons: safeParse('ReasonsData', getReasonsListData),
    contactMethod: safeParse('ContactMethodData', getContactMethodListData),
    appointmentType: safeParse('AppointmentTypeData', getListData),
    salutationOptions: safeParse('SalutationOptions', getSalutationOptions),
    appointmentMethod: safeParse('AppointmentMethodData', getListData),
    archiveReasons: (localStorageGetItem('ArchiveReasonsData') || []),
    financeTermOptions: safeParse('FinanceTermOptions', getFinanceTermOptions),
    reportType: safeParse('ReportTypeData', getListData),
    refundPaymentReason: safeParse('RefundPaymentReasonData', getListData),
    practiceChoiceCategory: safeParse('PracticeChoiceCategoryEnum'),
    usageType: safeParse('UsageTypeEnum'),
};

export const slice = createSlice({
    name: 'schema',
    initialState,
    reducers: {
        setPreferredContactMethod(state, action) {
            state.preferredContactMethodData = action.payload;
        },
        setReferral(state, action) {
            state.referral = action.payload;
        },
        setSourceType(state, action) {
            state.sourceType = action.payload;
        },
        setReminderMethod(state, action) {
            state.reminderMethod = action.payload;
        },
        setReminderPriority(state, action) {
            state.reminderPriority = action.payload;
        },
        setReminderGroup(state, action) {
            state.reminderGroup = action.payload || [];
        },
        setTaskMethod(state, action) {
            state.taskMethod = action.payload || [];
        },
        setTaskPriority(state, action) {
            state.taskPriority = action.payload || [];
        },
        setTaskCategory(state, action) {
            state.taskCategory = action.payload || [];
        },
        setReasons(state, action) {
            state.reasons = action.payload;
        },
        setArchiveReasons(state, action) {
            state.archiveReasons = action.payload;
        },
        setContactMethod(state, action) {
            state.contactMethod = action.payload;
        },
        setAppointmentType(state, action) {
            state.appointmentType = action.payload;
        },
        setSalutationOptions(state, action) {
            state.salutationOptions = action.payload;
        },
        setAppointmentMethod(state, action) {
            state.appointmentMethod = action.payload;
        },
        setFinanceTermOptions(state, action) {
            state.financeTermOptions = action.payload;
        },
        setReportType(state, action) {
            state.reportType = action.payload;
        },
        setRefundPaymentReason(state, action) {
            state.refundPaymentReason = action.payload;
        },
        setPracticeChoiceCategory(state, action) {
            state.practiceChoiceCategory = action.payload || [];
        },
        setUsageType(state, action) {
            state.usageType = action.payload || [];
        },
    }
});

export default slice.reducer;

export function getSchemaList() {
    return async () => {
        try {
            const response = await callApi('/api/v1/utils/enums/');
            if (response.status === 200) {
                const data = response?.data || {};

                const preferredContact = data.PreferredContactMethodData;
                dispatch(slice.actions.setPreferredContactMethod(getListData(preferredContact)));
                localStorageSetItem('PreferredContactMethodData', JSON.stringify(preferredContact));

                const referral = data.ReferralData;
                dispatch(slice.actions.setReferral(getReferralData(referral)));
                localStorageSetItem('ReferralData', JSON.stringify(referral));

                const sourceType = data.SourceTypeData;
                dispatch(slice.actions.setSourceType(getSourceTypeData(sourceType)));
                localStorageSetItem('SourceTypeData', JSON.stringify(sourceType));

                const reminderMethod = data.ReminderMethodData || [];
                dispatch(slice.actions.setReminderMethod(getReminderMethodData(reminderMethod)));
                localStorageSetItem('ReminderMethodData', JSON.stringify(reminderMethod));

                const reminderPriority = data.ReminderPriorityData;
                dispatch(slice.actions.setReminderPriority(getListData(reminderPriority)));
                localStorageSetItem('ReminderPriorityData', JSON.stringify(reminderPriority));

                const reminderGroup = data.ReminderGroupData;
                dispatch(slice.actions.setReminderGroup(reminderGroup || []));
                localStorageSetItem('ReminderGroupData', JSON.stringify(reminderGroup || []));

                // V2 Task* enums — lowercase values, used by /tasks/ endpoint.
                const taskMethod = data.TaskMethodData;
                dispatch(slice.actions.setTaskMethod(taskMethod || []));
                localStorageSetItem('TaskMethodData', JSON.stringify(taskMethod || []));

                const taskPriority = data.TaskPriorityData;
                dispatch(slice.actions.setTaskPriority(taskPriority || []));
                localStorageSetItem('TaskPriorityData', JSON.stringify(taskPriority || []));

                const taskCategory = data.TaskCategoryData;
                dispatch(slice.actions.setTaskCategory(taskCategory || []));
                localStorageSetItem('TaskCategoryData', JSON.stringify(taskCategory || []));

                const notes = data.NoteData;
                const reasons = notes?.filter((ele) => (ele !== 'AWAITING_RESPONSE' && ele !== 'FOLLOW_UP'));
                dispatch(slice.actions.setReasons(getReasonsListData(reasons)));
                localStorageSetItem('ReasonsData', JSON.stringify(reasons));
                // Store the full Note enum for the AppointmentPage reason picker
                // ("Spoke - Not Booked" follow-up flow). These values go to the
                // /notes/ endpoint (accepts any Note enum value) — NOT the
                // /archive/ endpoint.
                dispatch(slice.actions.setArchiveReasons(notes || []));
                localStorageSetItem('ArchiveReasonsData', JSON.stringify(notes || []));

                const contactedVia = data.ContactMethodData;
                dispatch(slice.actions.setContactMethod(getContactMethodListData(contactedVia)));
                localStorageSetItem('ContactMethodData', JSON.stringify(contactedVia));

                const appointmentType = data.AppointmentTypeData;
                dispatch(slice.actions.setAppointmentType(getListData(appointmentType)));
                localStorageSetItem('AppointmentTypeData', JSON.stringify(appointmentType));

                const salutation = data.SalutationOptions;
                dispatch(slice.actions.setSalutationOptions(getSalutationOptions(salutation)));
                localStorageSetItem('SalutationOptions', JSON.stringify(salutation));

                const appointmentMethod = data.AppointmentMethodData;
                dispatch(slice.actions.setAppointmentMethod(getListData(appointmentMethod)));
                localStorageSetItem('AppointmentMethodData', JSON.stringify(appointmentMethod));

                const financeTerm = data.FinanceTermOptions;
                dispatch(slice.actions.setFinanceTermOptions(getFinanceTermOptions(financeTerm)));
                localStorageSetItem('FinanceTermOptions', JSON.stringify(financeTerm));

                const reportType = data.ReportTypeData;
                dispatch(slice.actions.setReportType(getListData(reportType)));
                localStorageSetItem('ReportTypeData', JSON.stringify(reportType));

                const refundReason = data.RefundPaymentReasonData;
                dispatch(slice.actions.setRefundPaymentReason(getListData(refundReason)));
                localStorageSetItem('RefundPaymentReasonData', JSON.stringify(refundReason));

                const practiceCategory = data.PracticeChoiceCategoryEnum || [];
                dispatch(slice.actions.setPracticeChoiceCategory(practiceCategory));
                localStorageSetItem('PracticeChoiceCategoryEnum', JSON.stringify(practiceCategory));

                const usageType = data.UsageTypeEnum || [];
                dispatch(slice.actions.setUsageType(usageType));
                localStorageSetItem('UsageTypeEnum', JSON.stringify(usageType));
            } else {
                dispatch(updateError({ message: JSON.stringify(response?.data), success: false }));
            }
        } catch (error) {
            console.error("Error Occured in Schema.js ::", error);
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
    };
}
